/* Sogni ed Idee — comportamento del tema.
 *
 * Il carrello parla la stessa forma dati della Cart AJAX API di Shopify
 * ({ items, item_count, total_price }). Nell'anteprima lo stato vive in
 * localStorage; su Shopify basta sostituire le funzioni di Carrello.leggi e
 * Carrello.scrivi con fetch a /cart.js, /cart/add.js e /cart/change.js.
 * L'interfaccia non cambia di una riga.
 */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const euro = (centesimi) =>
    `${(centesimi / 100).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;

  const config = {
    soglia: 6000, // centesimi: spedizione gratuita da 60 €
    spedizione: 350,
  };

  // ------------------------------------------------------------- carrello
  //
  // Su Shopify il carrello e' quello vero: /cart.js per leggerlo, /cart/add.js
  // e /cart/change.js per modificarlo. Nell'anteprima statica, dove quegli
  // indirizzi non esistono, lo stato vive in localStorage. Le due sorgenti
  // restituiscono la stessa forma, quindi il resto del file non le distingue.
  const suShopify = typeof window.Shopify !== 'undefined';

  /**
   * Immagine per il carrello, ritagliata a 4:5 da Shopify. Chiedendo solo la
   * larghezza, il CDN manteneva le proporzioni originali della foto: dentro un
   * riquadro 4:5 con object-cover il fondo finiva tagliato. Con width+height
   * l'immagine arriva già 4:5 e riempie il riquadro senza scarti.
   */
  function immagineCarrello(url, larghezza = 200) {
    if (!url) return '';
    const assoluta = url.startsWith('//') ? `https:${url}` : url;
    if (!assoluta.includes('cdn.shopify.com')) return assoluta;
    const separatore = assoluta.includes('?') ? '&' : '?';
    return `${assoluta}${separatore}width=${larghezza}&height=${larghezza * 1.25}&crop=center`;
  }

  const Carrello = {
    chiave: 'sogni-ed-idee:carrello',
    _stato: { items: [], total_price: 0, item_count: 0 },

    /** Da risposta di /cart.js alla forma usata dall'interfaccia. */
    normalizza(dati) {
      return {
        items: (dati.items || []).map((i) => ({
          key: i.key,
          id: i.variant_id ?? i.id,
          title: i.product_title ?? i.title,
          variant_title: i.variant_title || '',
          price: i.price,
          quantity: i.quantity,
          image: immagineCarrello(i.image || i.featured_image?.url),
          url: i.url,
          properties: i.properties || {},
        })),
        total_price: dati.total_price ?? 0,
        item_count: dati.item_count ?? 0,
      };
    },

    calcola(items) {
      return {
        items,
        total_price: items.reduce((s, i) => s + i.price * i.quantity, 0),
        item_count: items.reduce((s, i) => s + i.quantity, 0),
      };
    },

    leggiLocale() {
      try {
        const grezzo = JSON.parse(localStorage.getItem(this.chiave));
        return Array.isArray(grezzo?.items) ? grezzo.items : [];
      } catch {
        return [];
      }
    },

    scriviLocale(items) {
      localStorage.setItem(this.chiave, JSON.stringify({ items }));
      this._stato = this.calcola(items);
      this.notifica();
    },

    async aggiorna() {
      if (!suShopify) {
        this._stato = this.calcola(this.leggiLocale());
      } else {
        const risposta = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        this._stato = this.normalizza(await risposta.json());
      }
      this.notifica();
    },

    async aggiungi(riga) {
      if (!suShopify) {
        const items = this.leggiLocale();
        const uguale = (i) =>
          i.id === riga.id && (i.properties?.Incisione || '') === (riga.properties?.Incisione || '');
        const esistente = items.find(uguale);
        if (esistente) esistente.quantity += riga.quantity;
        else items.push(riga);
        return this.scriviLocale(items);
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: riga.id, quantity: riga.quantity, properties: riga.properties }],
        }),
      });
      return this.aggiorna();
    },

    async cambia(chiave, quantita) {
      if (!suShopify) {
        const items = this.leggiLocale();
        const indice = items.findIndex((i) => i.key === chiave);
        if (indice === -1) return undefined;
        if (quantita <= 0) items.splice(indice, 1);
        else items[indice].quantity = quantita;
        return this.scriviLocale(items);
      }

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: chiave, quantity: quantita }),
      });
      return this.aggiorna();
    },

    stato() {
      return this._stato;
    },

    ascoltatori: [],
    osserva(fn) {
      this.ascoltatori.push(fn);
      fn(this.stato());
    },
    notifica() {
      const s = this.stato();
      this.ascoltatori.forEach((fn) => fn(s));
    },
  };

  // ------------------------------------------------- pannelli (menu, cart)
  function creaPannello({ radice, pannello, overlay, aperture, chiusure }) {
    const contenitore = $(radice);
    if (!contenitore) return null;
    const pan = $(pannello, contenitore);
    const ovl = $(overlay, contenitore);
    const direzione = pannello.includes('menu') ? '-translate-x-full' : 'translate-x-full';

    // Un solo interruttore di stato: aria-hidden. I pointer-events li decide il
    // CSS a partire da quello (vedi .strato-pannello).
    let apertoDa = null;

    const apri = (evento) => {
      apertoDa = evento?.currentTarget || null;
      contenitore.setAttribute('aria-hidden', 'false');
      ovl.classList.replace('opacity-0', 'opacity-100');
      pan.classList.remove(direzione);
      document.body.style.overflow = 'hidden';
      pan.querySelector('button, a, input')?.focus({ preventScroll: true });
    };

    const chiudi = () => {
      contenitore.setAttribute('aria-hidden', 'true');
      ovl.classList.replace('opacity-100', 'opacity-0');
      pan.classList.add(direzione);
      document.body.style.overflow = '';
      apertoDa?.focus({ preventScroll: true });
      apertoDa = null;
    };

    $$(aperture).forEach((b) => b.addEventListener('click', apri));
    ovl.addEventListener('click', chiudi);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && contenitore.getAttribute('aria-hidden') === 'false') chiudi();
    });

    // I pulsanti di chiusura vengono delegati: alcuni stanno dentro parti che
    // theme.js ridisegna (le righe del carrello), quindi legarli una volta sola
    // all'avvio non basterebbe.
    contenitore.addEventListener('click', (e) => {
      if (e.target.closest(chiusure)) chiudi();
    });

    return { apri, chiudi };
  }

  const pannelloCarrello = creaPannello({
    radice: '#cart-drawer',
    pannello: '[data-cart-panel]',
    overlay: '[data-cart-overlay]',
    aperture: '[data-cart-apri]',
    chiusure: '[data-cart-close]',
  });

  creaPannello({
    radice: '#menu-mobile',
    pannello: '[data-menu-pannello]',
    overlay: '[data-menu-overlay]',
    aperture: '[data-menu-apri]',
    chiusure: '[data-menu-chiudi]',
  });

  // ------------------------------------------------- barra spedizione
  function aggiornaSpedizione(totale) {
    const mancante = Math.max(0, config.soglia - totale);
    const percentuale = Math.min(100, (totale / config.soglia) * 100);

    $$('[data-spedizione-barra]').forEach((b) => (b.style.width = `${percentuale}%`));
    $$('[data-spedizione-testo]').forEach((t) => {
      t.innerHTML =
        mancante === 0
          ? '<strong>Spedizione gratuita.</strong> Ci pensiamo noi.'
          : `Aggiungi <strong>${euro(mancante)}</strong> per la spedizione gratuita.`;
    });
  }

  // ------------------------------------------------- righe del carrello
  function rigaHTML(riga, compatta) {
    const incisione = riga.properties?.Incisione
      ? `<p class="mt-0.5 text-xs text-inchiostro-tenue">Incisione: “${riga.properties.Incisione}”</p>`
      : '';
    return `
      <div class="flex gap-4 border-b border-inchiostro/10 py-5" data-riga="${riga.key}">
        <a href="${riga.url}" class="block w-20 shrink-0 bg-sabbia">
          <img src="${riga.image}" alt="" width="160" height="200" loading="lazy" class="aspect-4/5 w-full object-cover">
        </a>
        <div class="min-w-0 flex-1">
          <a href="${riga.url}" class="link-filo font-display ${compatta ? 'text-base' : 'text-lg'}" style="font-variation-settings:'opsz' 20">${riga.title}</a>
          <p class="mt-0.5 text-xs text-inchiostro-tenue">${riga.variant_title}</p>
          ${incisione}
          <div class="mt-3 flex items-center justify-between gap-3">
            <div class="flex items-center border border-inchiostro/20">
              <button type="button" class="px-2.5 py-1.5 text-inchiostro-tenue hover:text-inchiostro" data-riga-meno aria-label="Diminuisci">−</button>
              <span class="min-w-8 text-center text-sm">${riga.quantity}</span>
              <button type="button" class="px-2.5 py-1.5 text-inchiostro-tenue hover:text-inchiostro" data-riga-piu aria-label="Aumenta">+</button>
            </div>
            <span class="text-sm">${euro(riga.price * riga.quantity)}</span>
          </div>
          <button type="button" class="mt-2 text-xs text-inchiostro-tenue underline underline-offset-2 hover:text-inchiostro" data-riga-rimuovi>Rimuovi</button>
        </div>
      </div>`;
  }

  function disegnaRighe(contenitore, stato, compatta) {
    if (!contenitore) return;
    contenitore.innerHTML = stato.items.length
      ? stato.items.map((r) => rigaHTML(r, compatta)).join('')
      : `<p class="${compatta ? 'py-16' : 'border-y border-inchiostro/12 py-20'} text-center text-sm text-inchiostro-tenue">Il carrello è vuoto.</p>`;

    $$('[data-riga]', contenitore).forEach((el) => {
      const chiave = el.dataset.riga;
      const riga = stato.items.find((i) => i.key === chiave);
      $('[data-riga-meno]', el)?.addEventListener('click', () => Carrello.cambia(chiave, riga.quantity - 1));
      $('[data-riga-piu]', el)?.addEventListener('click', () => Carrello.cambia(chiave, riga.quantity + 1));
      $('[data-riga-rimuovi]', el)?.addEventListener('click', () => Carrello.cambia(chiave, 0));
    });
  }

  Carrello.osserva((stato) => {
    // Contatore nell'intestazione
    $$('[data-cart-conteggio]').forEach((el) => {
      el.textContent = stato.item_count;
      el.classList.toggle('hidden', stato.item_count === 0);
    });

    $$('[data-cart-totale]').forEach((el) => (el.textContent = euro(stato.total_price)));
    disegnaRighe($('[data-cart-righe]'), stato, true);
    disegnaRighe($('[data-carrello-pagina-righe]'), stato, false);
    aggiornaSpedizione(stato.total_price);

    // Riepilogo della pagina carrello
    const spedizione = stato.total_price >= config.soglia || stato.total_price === 0 ? 0 : config.spedizione;
    $$('[data-carrello-subtotale]').forEach((el) => (el.textContent = euro(stato.total_price)));
    $$('[data-carrello-spedizione]').forEach((el) => {
      el.textContent = stato.total_price >= config.soglia ? 'Gratuita' : euro(config.spedizione);
    });
    $$('[data-carrello-totale]').forEach((el) => (el.textContent = euro(stato.total_price + spedizione)));
  });

  // ------------------------------------------------- varianti del prodotto
  const varianti = (() => {
    const dati = $('[data-varianti]');
    if (!dati) return null;
    try {
      return JSON.parse(dati.textContent);
    } catch {
      return null;
    }
  })();

  /** La variante che corrisponde alle opzioni spuntate nella scheda. */
  function varianteScelta() {
    if (!varianti) return null;
    // Ordinate per posizione dell'opzione: `opzioni` nel JSON segue l'ordine
    // dichiarato su Shopify, non quello in cui compaiono i pulsanti.
    const scelte = $$('[data-opzione]:checked')
      .sort((a, b) => Number(a.dataset.opzione || 0) - Number(b.dataset.opzione || 0))
      .map((i) => i.value);
    if (!scelte.length) return varianti[0];
    return (
      varianti.find((v) => v.opzioni.length === scelte.length && v.opzioni.every((o, i) => o === scelte[i])) ||
      null
    );
  }

  function aggiornaVariante() {
    const v = varianteScelta();
    const bottone = $('[data-form-prodotto] [data-aggiungi]');
    if (!v || !bottone) return;

    bottone.dataset.variantId = v.id;
    bottone.dataset.variante = v.titolo;
    bottone.dataset.prezzo = v.prezzo;

    const esaurita = v.disponibile === false;
    $$('[data-aggiungi]').forEach((b) => {
      b.disabled = esaurita;
      if (esaurita) b.dataset.testoOriginale ||= b.textContent.trim();
      b.textContent = esaurita ? 'Esaurito' : b.dataset.testoOriginale || b.textContent;
    });

    // L'immagine da mandare al carrello segue la variante, non piu' la foto
    // di copertina: senza questo, in carrello finiva sempre la stessa foto.
    if (v.immagineCarrello) {
      $$('[data-aggiungi]').forEach((b) => (b.dataset.immagine = v.immagineCarrello));
    }

    const prezzo = $('[data-prezzo-corrente]');
    if (prezzo) prezzo.textContent = euro(v.prezzo);

    const barra = $('[data-prezzo-barra]');
    if (barra) barra.textContent = euro(v.prezzo);
    const varianteBarra = $('[data-variante-barra]');
    if (varianteBarra) varianteBarra.textContent = v.titolo ? `· ${v.titolo}` : '';

    // Il prezzo barrato e lo sconto esistono sempre nel markup ma partono
    // nascosti: una variante in saldo puo' venire dopo una a prezzo pieno.
    const confronto = $('[data-prezzo-confronto]');
    const sconto = $('[data-prezzo-sconto]');
    const inSaldo = v.confronto > v.prezzo;
    if (confronto) {
      confronto.textContent = euro(v.confronto);
      confronto.classList.toggle('hidden', !inSaldo);
    }
    if (sconto) {
      sconto.textContent = `−${Math.round(((v.confronto - v.prezzo) * 100) / v.confronto)}%`;
      sconto.classList.toggle('hidden', !inSaldo);
    }

    mostraFotoVariante(v);
  }

  /**
   * Porta in primo piano la foto della variante scelta. Se quella foto e' anche
   * nella galleria, sposta li' la miniatura attiva invece di lasciarne due
   * scollegate.
   */
  function mostraFotoVariante(v) {
    if (!v.immagine) return;
    const principale = $('#foto-principale');
    if (!principale) return;

    // Confronto sull'attributo, non su `src`: la proprieta' restituisce
    // l'indirizzo assoluto e non combacerebbe mai con quello stampato da Liquid.
    if (principale.getAttribute('src') !== v.immagine) principale.src = v.immagine;
    if (v.alt) principale.alt = v.alt;
    $$('[data-miniatura]').forEach((b) => {
      b.dataset.attiva = String(b.dataset.miniatura === v.immagine);
    });
  }

  if (varianti) {
    $$('[data-opzione]').forEach((i) => i.addEventListener('change', aggiornaVariante));
    aggiornaVariante();
  }

  // ------------------------------------------------- aggiungi al carrello
  document.addEventListener('click', async (e) => {
    const bottone = e.target.closest('[data-aggiungi]');
    if (!bottone || bottone.disabled) return;

    const modulo = bottone.closest('form') || document;
    const incisione = $('[data-proprieta]', modulo)?.value?.trim() || '';
    const quantita = Number($('[data-qta-valore]', modulo)?.value || 1);

    const scelta = varianteScelta();
    const idVariante = Number(scelta?.id || bottone.dataset.variantId);
    const titoloVariante = scelta?.titolo || bottone.dataset.variante;

    const originale = bottone.textContent;
    bottone.textContent = 'Aggiungo…';

    await Carrello.aggiungi({
      id: idVariante,
      key: `${idVariante}:${incisione}`,
      title: bottone.dataset.titolo,
      variant_title: titoloVariante,
      price: Number(scelta?.prezzo ?? bottone.dataset.prezzo),
      quantity: quantita,
      image: bottone.dataset.immagine,
      url: bottone.dataset.url,
      properties: incisione ? { Incisione: incisione } : {},
    });

    bottone.textContent = 'Aggiunto ✓';
    setTimeout(() => (bottone.textContent = originale), 1200);
    pannelloCarrello?.apri();
  });

  // ------------------------------------------------- scheda prodotto
  $$('[data-miniatura]').forEach((bottone) => {
    bottone.addEventListener('click', () => {
      const principale = $('#foto-principale');
      if (!principale) return;
      principale.src = bottone.dataset.miniatura;
      principale.alt = bottone.dataset.alt;
      $$('[data-miniatura]').forEach((b) => (b.dataset.attiva = String(b === bottone)));
    });
  });

  $$('[data-qta]').forEach((bottone) => {
    bottone.addEventListener('click', () => {
      const campo = $('[data-qta-valore]', bottone.closest('form') || document);
      if (!campo) return;
      const passo = bottone.dataset.qta === '+' ? 1 : -1;
      campo.value = Math.min(20, Math.max(1, Number(campo.value) + passo));
    });
  });

  // Barra d'acquisto mobile: compare quando il modulo principale esce di scena.
  const barraAcquisto = $('[data-barra-acquisto]');
  const formProdotto = $('[data-form-prodotto]');
  if (barraAcquisto) {
    // La barra e' fissa: senza questo spazio copre l'ultima riga del piede.
    const soloMobile = window.matchMedia('(max-width: 767px)');
    const spazio = () => {
      document.body.style.paddingBottom = soloMobile.matches
        ? `${barraAcquisto.offsetHeight}px`
        : '';
    };
    spazio();
    soloMobile.addEventListener('change', spazio);
  }
  if (barraAcquisto && formProdotto && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      ([voce]) => barraAcquisto.classList.toggle('translate-y-full', voce.isIntersecting),
      { rootMargin: '-120px 0px 0px 0px' },
    ).observe(formProdotto);
  }

  // Pannello dei filtri: sempre aperto da lg in su, a fisarmonica sotto.
  const pannelloFiltri = $('[data-filtri-pannello]');
  if (pannelloFiltri) {
    const daLg = window.matchMedia('(min-width: 1024px)');
    const freccia = $('[data-filtri-freccia]', pannelloFiltri);
    const sincronizza = () => {
      if (daLg.matches) pannelloFiltri.open = true;
    };
    sincronizza();
    daLg.addEventListener('change', sincronizza);
    pannelloFiltri.addEventListener('toggle', () => {
      freccia?.classList.toggle('rotate-180', pannelloFiltri.open);
    });
    // Il pulsante "Azzera" vive dentro il riepilogo: senza questo, premerlo
    // richiuderebbe anche il pannello.
    $('[data-azzera-filtri]', pannelloFiltri)?.addEventListener('click', (e) => e.preventDefault());
  }

  // ------------------------------------------------- filtri di collezione
  const griglia = $('[data-griglia]');
  if (griglia) {
    const schede = $$('[data-prodotto]', griglia);
    const conteggio = $('[data-conteggio]');
    const vuoto = $('[data-nessun-risultato]');
    const ordina = $('[data-ordina]');

    const attivi = (nome) =>
      $$(`[data-filtro="${nome}"]:checked`).map((i) => i.value);

    const applica = () => {
      const tipi = attivi('tipo');
      const materiali = attivi('materiale');
      const fasce = attivi('prezzo');
      const tag = attivi('tag');
      let visibili = 0;

      schede.forEach((scheda) => {
        const prezzo = Number(scheda.dataset.prezzo);
        const inFascia =
          !fasce.length ||
          fasce.some((f) => {
            const [min, max] = f.split('-').map(Number);
            return prezzo >= min && prezzo <= max;
          });

        const passa =
          (!tipi.length || tipi.includes(scheda.dataset.tipo)) &&
          (!materiali.length || materiali.includes(scheda.dataset.materiale)) &&
          (!tag.length || tag.every((t) => scheda.dataset.tags.split(',').includes(t))) &&
          inFascia;

        scheda.hidden = !passa;
        if (passa) visibili++;
      });

      if (conteggio) conteggio.textContent = visibili;
      if (vuoto) vuoto.classList.toggle('hidden', visibili > 0);
    };

    $$('[data-filtro]').forEach((i) => i.addEventListener('change', applica));
    $$('[data-azzera-filtri]').forEach((b) =>
      b.addEventListener('click', () => {
        $$('[data-filtro]').forEach((i) => (i.checked = false));
        applica();
      }),
    );

    ordina?.addEventListener('change', () => {
      const criterio = ordina.value;
      const ordinate = [...schede].sort((a, b) => {
        if (criterio === 'prezzo-crescente') return a.dataset.prezzo - b.dataset.prezzo;
        if (criterio === 'prezzo-decrescente') return b.dataset.prezzo - a.dataset.prezzo;
        if (criterio === 'nome') return a.dataset.nome.localeCompare(b.dataset.nome, 'it');
        return 0;
      });
      ordinate.forEach((s) => griglia.appendChild(s));
    });
  }

  // ------------------------------------------------- comparsa in scroll
  const daRivelare = $$('.rivela');
  if ('IntersectionObserver' in window && daRivelare.length) {
    const osservatore = new IntersectionObserver(
      (voci) => {
        voci.forEach((voce) => {
          if (!voce.isIntersecting) return;
          voce.target.classList.add('visibile');
          osservatore.unobserve(voce.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    daRivelare.forEach((el) => osservatore.observe(el));

    // Rete di sicurezza: se qualcosa va storto, dopo un secondo si vede tutto.
    setTimeout(() => daRivelare.forEach((el) => el.classList.add('visibile')), 1000);
  } else {
    daRivelare.forEach((el) => el.classList.add('visibile'));
  }

  // Primo caricamento: su Shopify chiede lo stato reale a /cart.js, in
  // anteprima rilegge localStorage.
  Carrello.aggiorna();

  // ------------------------------------------------- moduli dell'anteprima
  // Nell'anteprima non c'e' backend: mostriamo la conferma senza inviare nulla.
  $$('form[data-form-type]').forEach((modulo) => {
    modulo.addEventListener('submit', (e) => {
      e.preventDefault();
      const bottone = $('button[type="submit"]', modulo);
      if (!bottone) return;
      const originale = bottone.textContent;
      bottone.textContent = 'Inviato ✓';
      bottone.disabled = true;
      modulo.insertAdjacentHTML(
        'beforeend',
        '<p class="mt-3 border border-salvia/40 bg-salvia-velo p-3 text-sm" role="status">Anteprima: il messaggio non è stato inviato davvero. Sul sito online arriverà a Paola.</p>',
      );
      setTimeout(() => {
        bottone.textContent = originale;
        bottone.disabled = false;
      }, 4000);
    });
  });
})();
